import { useEffect, useRef, useState } from 'react';
import { defaultTemplateSender, type TemplateSender } from '@/lib/printTemplates';
import { loadPrintSender, savePrintSender, type SavedPrintSender } from '@/lib/printSenderSettings';

/** Mounted within the organization/profile/access-keyed print workspace. */
export function usePrintSenderSettings(organizationId: string, canSave: boolean) {
  const [sender, setDraft] = useState<TemplateSender>(defaultTemplateSender);
  const [saved, setSaved] = useState<SavedPrintSender | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const request = useRef<AbortController | null>(null);
  const mounted = useRef(true);

  const setSender = (draft: TemplateSender) => {
    setDraft(draft);
    setNotice('');
    setError('');
  };
  const dirty = JSON.stringify(sender) !== JSON.stringify(saved?.sender ?? defaultTemplateSender());

  async function reload() {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError('');
    setNotice('');
    setReady(false);
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10000);
    try {
      const result = await loadPrintSender(organizationId, controller.signal);
      if (!mounted.current || controller.signal.aborted) return;
      setSaved(result);
      setDraft(result?.sender ?? defaultTemplateSender());
      setReady(true);
    } catch (failure) {
      if (mounted.current && (timedOut || !controller.signal.aborted))
        setError(
          timedOut
            ? 'Loading the saved address timed out. Your entries are kept. Use Reload saved address to retry.'
            : failure instanceof Error
              ? failure.message
              : 'The saved address could not be loaded. Please retry.',
        );
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) request.current = null;
      if (mounted.current && (timedOut || !controller.signal.aborted)) setLoading(false);
    }
  }

  async function save() {
    if (!canSave || !ready || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setSaving(true);
    setError('');
    setNotice('');
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10000);
    try {
      const result = await savePrintSender(
        organizationId,
        sender,
        saved?.version ?? null,
        controller.signal,
      );
      if (!mounted.current || controller.signal.aborted) return;
      setSaved(result);
      setDraft(result.sender);
      setNotice('Sender address saved. It is available for future prints.');
    } catch (failure) {
      if (mounted.current && (timedOut || !controller.signal.aborted)) {
        if (timedOut) setReady(false);
        setError(
          timedOut
            ? 'Saving timed out and could not be confirmed. Your entries are kept. Reload the saved address before saving again.'
            : failure instanceof Error
              ? failure.message
              : 'The address could not be saved. Your entries are kept.',
        );
      }
    } finally {
      clearTimeout(timeout);
      if (request.current === controller) request.current = null;
      if (mounted.current && (timedOut || !controller.signal.aborted)) setSaving(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => {
      mounted.current = false;
      request.current?.abort();
    };
  }, [organizationId]);

  return { sender, setSender, saved, dirty, loading, saving, ready, error, notice, reload, save };
}
